import type { ModuleId, ModuleSummary, TrialResult } from './types';

function finiteSorted(values: readonly number[]): number[] {
  return values.filter(Number.isFinite).sort((left, right) => left - right);
}

/** R-7/Excel-compatible quantile interpolation. */
export function quantile(values: readonly number[], probability: number): number | undefined {
  if (probability < 0 || probability > 1 || !Number.isFinite(probability)) {
    throw new RangeError('probability must be between 0 and 1');
  }
  const sorted = finiteSorted(values);
  if (sorted.length === 0) return undefined;
  if (sorted.length === 1) return sorted[0];
  const position = (sorted.length - 1) * probability;
  const lowerIndex = Math.floor(position);
  const fraction = position - lowerIndex;
  const lower = sorted[lowerIndex] as number;
  const upper = sorted[Math.min(lowerIndex + 1, sorted.length - 1)] as number;
  return lower + (upper - lower) * fraction;
}

export function median(values: readonly number[]): number | undefined {
  return quantile(values, 0.5);
}

/** Returns [Q1, Q3], not Q3-Q1, so raw spread remains inspectable. */
export function interquartileRange(values: readonly number[]): [number, number] | undefined {
  const first = quantile(values, 0.25);
  const third = quantile(values, 0.75);
  return first === undefined || third === undefined ? undefined : [first, third];
}

export function percentile(values: readonly number[], probability: number): number | undefined {
  return quantile(values, probability);
}

/** Wilson score interval for a binomial proportion. Empty samples return [0, 1]. */
export function wilsonInterval(
  successes: number,
  total: number,
  z = 1.959_963_984_540_054,
): [number, number] {
  if (!Number.isInteger(successes) || !Number.isInteger(total) || total < 0 || successes < 0 || successes > total) {
    throw new RangeError('successes and total must be valid non-negative counts');
  }
  if (total === 0) return [0, 1];
  const proportion = successes / total;
  const zSquared = z * z;
  const denominator = 1 + zSquared / total;
  const center = (proportion + zSquared / (2 * total)) / denominator;
  const radius =
    (z / denominator) *
    Math.sqrt((proportion * (1 - proportion) + zSquared / (4 * total)) / total);
  return [
    successes === 0 ? 0 : Math.max(0, center - radius),
    successes === total ? 1 : Math.min(1, center + radius),
  ];
}

export type MetricDirection = 'higher-is-better' | 'lower-is-better';

/** Converts a personal baseline to 100. A larger index always means improvement. */
export function baselineIndex(
  current: number,
  baseline: number,
  direction: MetricDirection = 'higher-is-better',
): number | undefined {
  if (!Number.isFinite(current) || !Number.isFinite(baseline) || current < 0 || baseline <= 0) {
    return undefined;
  }
  return direction === 'higher-is-better'
    ? (current / baseline) * 100
    : (baseline / Math.max(current, Number.EPSILON)) * 100;
}

export interface SummaryOptions {
  threshold?: number;
  thresholdUnit?: 'speed' | 'arcmin' | 'percent' | 'ms';
  baselineValue?: number;
  baselineDirection?: MetricDirection;
}

export function summarizeTrials(
  moduleId: ModuleId,
  trials: readonly TrialResult[],
  options: SummaryOptions = {},
): ModuleSummary {
  const moduleTrials = trials.filter((trial) => trial.moduleId === moduleId);
  const validTrials = moduleTrials.filter((trial) => trial.valid);
  const successes = validTrials.filter((trial) => trial.correct).length;
  const correctReactionTimes = validTrials
    .filter((trial) => trial.correct && trial.reactionTimeMs !== undefined)
    .map((trial) => trial.reactionTimeMs as number);
  const centralValue = options.threshold ?? (validTrials.length === 0 ? 0 : successes / validTrials.length);

  return {
    moduleId,
    validTrials: validTrials.length,
    accuracy: validTrials.length === 0 ? 0 : successes / validTrials.length,
    accuracyCi95: wilsonInterval(successes, validTrials.length),
    medianReactionTimeMs: median(correctReactionTimes),
    reactionTimeIqrMs: interquartileRange(correctReactionTimes),
    omissions: validTrials.filter((trial) => trial.response === undefined).length,
    falseResponses: validTrials.filter((trial) => !trial.correct && trial.response !== undefined).length,
    threshold: options.threshold,
    thresholdUnit: options.thresholdUnit,
    baselineIndex:
      options.baselineValue === undefined
        ? undefined
        : baselineIndex(centralValue, options.baselineValue, options.baselineDirection),
  };
}
